'use client';

import React, { useState } from 'react';
import { calculateNumerology, NumerologyResponse } from '@/lib/api/client';
import { NumerologyResultCard } from '@/components/numerology/NumerologyResultCard';
import { CalculatorPageContent } from '@/components/calculators/CalculatorPageContent';
import { User, Sparkles } from 'lucide-react';

export default function NameNumerologyCalculatorPage() {
  const [fullName, setFullName] = useState('John Doe');
  const [result, setResult] = useState<NumerologyResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const data = await calculateNumerology({ fullName });
      setResult(data);
    } catch (err: any) {
      setError(err.message || 'Failed to calculate Name Numerology numbers');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const introParagraph = "Name Numerology analyzes the acoustic and vibrational patterns of your name. Using Pythagorean letter-value assignment (A=1, B=2... I=9), it calculates your Expression (Destiny), Soul Urge, and Personality numbers.";

  const methodologyContent = (
    <div className="space-y-3">
      <p>
        I assign numerical values 1–9 to the English alphabet sequentially. Vowels (A, E, I, O, U) sum to your Soul Urge Number, consonants sum to your Personality Number, and the total name sum reveals your Expression Number.
      </p>
    </div>
  );

  const subsections = [
    {
      title: "Expression (Destiny) Number",
      content: "Derived from all letters in your full name, the Expression Number represents your natural talents, physical potential, and overall life expression."
    },
    {
      title: "Soul Urge (Heart's Desire) Number",
      content: "Calculated exclusively from the vowels in your name, this number reveals your private inner motivations, emotional longings, and core desire."
    },
    {
      title: "Personality Number",
      content: "Calculated from the consonants in your name, the Personality Number describes your external social presence and how others perceive you initially."
    }
  ];

  const faqs = [
    {
      q: "What is Name Numerology?",
      a: "Name Numerology converts the letters of your full name into numbers to calculate your Expression, Soul Urge, and Personality vibrations."
    },
    {
      q: "Should I use my birth name or current married name?",
      a: "Pythagorean numerologists recommend using your full birth name as recorded on official records for core life destiny, though current names reflect active social presence."
    },
    {
      q: "How are vowels vs consonants calculated?",
      a: "Vowels determine your Soul Urge Number, consonants determine your Personality Number, and their combined total creates your Expression Number."
    },
    {
      q: "Is this Name Numerology Calculator free?",
      a: "Yes, 100%! All calculations on AstroPredict are completely free."
    }
  ];

  const relatedCalculators = [
    {
      title: "Numerology Calculator",
      href: "/numerology-calculator",
      description: "Calculate your Life Path and Birthday numbers from your date of birth.",
      categoryBadge: "Date"
    },
    {
      title: "Birth Chart Calculator",
      href: "/birth-chart-calculator",
      description: "Explore your 12-house Vedic birth chart alongside name numerology.",
      categoryBadge: "Vedic"
    },
    {
      title: "Moon Sign Calculator",
      href: "/moon-sign-calculator",
      description: "Find your Vedic Moon sign for emotional alignment.",
      categoryBadge: "Vedic"
    }
  ];

  return (
    <div className="space-y-8 max-w-4xl mx-auto py-4">
      {/* Header Banner */}
      <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 sm:p-8 backdrop-blur-xl shadow-2xl relative overflow-hidden">
        <div className="flex items-center gap-2 text-[#5ab4ff] text-[11px] font-bold uppercase tracking-wider mb-2">
          <Sparkles className="w-4 h-4 text-[#5ab4ff]" />
          <span>Pythagorean Name Vibration</span>
        </div>
        <h1 className="text-3xl font-heading font-semibold text-white mb-2">
          Name Numerology Calculator
        </h1>
        <p className="text-[rgba(220,236,255,0.7)] text-sm max-w-2xl leading-relaxed">
          Calculate your Expression (Destiny), Soul Urge (Vowels), and Personality (Consonants) numbers from your full birth name using Pythagorean letter-to-number mapping.
        </p>
      </div>

      {/* Input Form */}
      <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 backdrop-blur-xl shadow-xl">
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row items-end gap-4">
          <div className="flex-1 w-full">
            <label htmlFor="name-input" className="block text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-2">
              <User className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5 text-[#5ab4ff]" />
              Full Name
            </label>
            <input
              id="name-input"
              type="text"
              required
              placeholder="e.g. John Doe"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 px-4 text-white text-sm placeholder-[rgba(220,236,255,0.7)] focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] transition-all"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto h-12 px-8 rounded-full bg-white text-[#03163d] font-semibold text-sm hover:bg-[#b9c6ff] shadow-[0_0_25px_rgba(150,165,255,0.4)] disabled:opacity-50 transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ab4ff] active:scale-[0.98] shrink-0"
          >
            {loading ? 'Calculating...' : 'Calculate Name Numbers'}
          </button>
        </form>
        {error && <p className="mt-3 text-xs text-[#ff9bb0]">{error}</p>}
      </div>

      {/* Results Section */}
      {result && (
        <div className="space-y-6">
          {result.destinyNumber && (
            <NumerologyResultCard label="Expression Number (Destiny)" detail={result.destinyNumber} />
          )}
          {result.soulUrgeNumber && (
            <NumerologyResultCard label="Soul Urge Number (Heart's Desire)" detail={result.soulUrgeNumber} />
          )}
          {result.personalityNumber && (
            <NumerologyResultCard label="Personality Number" detail={result.personalityNumber} />
          )}
        </div>
      )}

      <CalculatorPageContent
        calculatorName="Name Numerology Calculator"
        introParagraph={introParagraph}
        methodologyTitle="How I Calculate Your Name Numerology"
        methodologyContent={methodologyContent}
        subsections={subsections}
        faqs={faqs}
        relatedCalculators={relatedCalculators}
        showSignPeriodGrid={false}
      />
    </div>
  );
}
